import {
	type ScrollToOptions,
	useVirtualizer,
	type VirtualItem,
	type Virtualizer,
	type VirtualizerOptions
} from '@tanstack/react-virtual'
import {
	type RefObject,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'

export type VirtualAxis = 'vertical' | 'horizontal'
export type VirtualThreshold = { items: number } | { px: number }

const DEFAULT_VERTICAL_THRESHOLD: VirtualThreshold = { items: 3 }
const DEFAULT_HORIZONTAL_THRESHOLD: VirtualThreshold = { px: 300 }

interface AxisConfig<TItem extends Element> {
	count: number
	estimateSize?: VirtualizerOptions<HTMLDivElement, TItem>['estimateSize']
	overscan?: number
	getItemKey?: VirtualizerOptions<HTMLDivElement, TItem>['getItemKey']
	initialOffset?: VirtualizerOptions<HTMLDivElement, TItem>['initialOffset']
	scrollMargin?: VirtualizerOptions<HTMLDivElement, TItem>['scrollMargin']
	threshold?: VirtualThreshold
	onStartReached?: () => unknown
	onEndReached?: () => unknown
}

type UseVirtualizationOptions<TItem extends Element> = {
	scrollRef: RefObject<HTMLDivElement | null>
	vertical?: AxisConfig<TItem>
	horizontal?: AxisConfig<TItem>
} & (
	| { vertical: AxisConfig<TItem>; horizontal?: AxisConfig<TItem> }
	| { horizontal: AxisConfig<TItem>; vertical?: AxisConfig<TItem> }
)

type AxisVirtualizer<TItem extends Element> = Virtualizer<HTMLDivElement, TItem>

const EMPTY_VIRTUAL_SNAPSHOT = makeVirtualSnapshot([], '', 0)
type VirtualSnapshot = ReturnType<typeof makeVirtualSnapshot>

export interface VirtualizationApi {
	getScrollElement: () => HTMLDivElement | null
	getTotalSize: (axis: VirtualAxis) => number
	scrollToStart: (axis: VirtualAxis, opts?: ScrollToOptions) => void
	scrollToEnd: (axis: VirtualAxis, opts?: ScrollToOptions) => void
	scrollToIndex: (
		axis: VirtualAxis,
		index: number,
		opts?: ScrollToOptions
	) => void
	scrollToOffset: (
		offset: Partial<Record<VirtualAxis, number>>,
		opts?: ScrollToOptions
	) => void
}

export function useVirtualization<TItem extends Element = HTMLDivElement>({
	scrollRef,
	vertical,
	horizontal
}: UseVirtualizationOptions<TItem>) {
	const [verticalSnapshot, setVerticalSnapshot] = useState(
		EMPTY_VIRTUAL_SNAPSHOT
	)
	const [horizontalSnapshot, setHorizontalSnapshot] = useState(
		EMPTY_VIRTUAL_SNAPSHOT
	)
	const verticalCount = vertical?.count
	const horizontalCount = horizontal?.count
	const verticalCountRef = useRef(verticalCount)
	const horizontalCountRef = useRef(horizontalCount)
	verticalCountRef.current = verticalCount
	horizontalCountRef.current = horizontalCount
	const verticalVirtualizer = useAxisVirtualizer({
		axis: 'vertical',
		config: vertical,
		scrollRef,
		onChange: (virtualizer) => {
			updateVirtualSnapshot({
				virtualizer,
				setSnapshot: setVerticalSnapshot
			})
		}
	})
	const horizontalVirtualizer = useAxisVirtualizer({
		axis: 'horizontal',
		config: horizontal,
		scrollRef,
		onChange: (virtualizer) => {
			updateVirtualSnapshot({
				virtualizer,
				setSnapshot: setHorizontalSnapshot
			})
		}
	})

	useLayoutEffect(() => {
		updateVirtualSnapshot({
			virtualizer: verticalVirtualizer,
			setSnapshot: setVerticalSnapshot
		})
		updateVirtualSnapshot({
			virtualizer: horizontalVirtualizer,
			setSnapshot: setHorizontalSnapshot
		})
	})

	useVirtualEdges({
		virtualizer: verticalVirtualizer,
		snapshot: verticalSnapshot,
		threshold: vertical?.threshold ?? DEFAULT_VERTICAL_THRESHOLD,
		onStartReached: vertical?.onStartReached,
		onEndReached: vertical?.onEndReached
	})
	useVirtualEdges({
		virtualizer: horizontalVirtualizer,
		snapshot: horizontalSnapshot,
		threshold: horizontal?.threshold ?? DEFAULT_HORIZONTAL_THRESHOLD,
		onStartReached: horizontal?.onStartReached,
		onEndReached: horizontal?.onEndReached
	})

	const apiRef = useRef<VirtualizationApi | null>(null)
	if (apiRef.current === null) {
		apiRef.current = {
			getScrollElement: () => scrollRef.current,
			getTotalSize: (axis) =>
				axis === 'vertical'
					? verticalVirtualizer.getTotalSize()
					: horizontalVirtualizer.getTotalSize(),
			scrollToStart: (axis, opts) => {
				if (axis === 'vertical') {
					verticalVirtualizer.scrollToOffset(0, opts)
					return
				}

				horizontalVirtualizer.scrollToOffset(0, opts)
			},
			scrollToEnd: (axis, opts) => {
				const count =
					axis === 'vertical'
						? verticalCountRef.current
						: horizontalCountRef.current

				if (!count) {
					return
				}
				if (axis === 'vertical') {
					verticalVirtualizer.scrollToIndex(count - 1, opts)
					return
				}

				horizontalVirtualizer.scrollToIndex(count - 1, opts)
			},
			scrollToIndex: (axis, index, opts) => {
				if (axis === 'vertical') {
					verticalVirtualizer.scrollToIndex(index, opts)
					return
				}

				horizontalVirtualizer.scrollToIndex(index, opts)
			},
			scrollToOffset: (offset, opts) => {
				if (offset.vertical !== undefined) {
					verticalVirtualizer.scrollToOffset(offset.vertical, opts)
				}
				if (offset.horizontal !== undefined) {
					horizontalVirtualizer.scrollToOffset(
						offset.horizontal,
						opts
					)
				}
			}
		}
	}

	return {
		api: apiRef.current,
		vertical: {
			virtualizer: verticalVirtualizer,
			items: verticalSnapshot.items,
			totalSize: verticalSnapshot.totalSize
		},
		horizontal: {
			virtualizer: horizontalVirtualizer,
			items: horizontalSnapshot.items,
			totalSize: horizontalSnapshot.totalSize
		}
	}
}

function useAxisVirtualizer<TItem extends Element>({
	axis,
	config,
	scrollRef,
	onChange
}: {
	axis: VirtualAxis
	config: AxisConfig<TItem> | undefined
	scrollRef: RefObject<HTMLDivElement | null>
	onChange: (virtualizer: AxisVirtualizer<TItem>) => void
}) {
	return useVirtualizer<HTMLDivElement, TItem>({
		count: config?.count ?? 0,
		getScrollElement: () => scrollRef.current,
		estimateSize:
			config?.estimateSize ??
			(axis === 'vertical' ? () => 44 : () => 160),
		overscan: config?.overscan ?? (axis === 'vertical' ? 8 : 3),
		getItemKey: config?.getItemKey ?? ((index) => index),
		initialOffset: config?.initialOffset,
		scrollMargin: config?.scrollMargin,
		horizontal: axis === 'horizontal',
		useAnimationFrameWithResizeObserver: true,
		useFlushSync: false,
		enabled: config !== undefined,
		onChange: (virtualizer) => onChange(virtualizer)
	})
}

function updateVirtualSnapshot<TItem extends Element>({
	virtualizer,
	setSnapshot
}: {
	virtualizer: AxisVirtualizer<TItem>
	setSnapshot: (
		updater: (current: VirtualSnapshot) => VirtualSnapshot
	) => void
}) {
	const items = virtualizer.getVirtualItems()
	const first = items[0]
	const last = items.at(-1)
	const totalSize = virtualizer.getTotalSize()
	const signature = [
		totalSize,
		items.length,
		first?.key,
		first?.start,
		last?.key,
		last?.end
	].join('|')
	setSnapshot((current) =>
		current.signature === signature
			? current
			: makeVirtualSnapshot(items, signature, totalSize)
	)
}

function makeVirtualSnapshot(
	items: VirtualItem[],
	signature: string,
	totalSize: number
) {
	return { items, signature, totalSize } as const
}

function useVirtualEdges<TItem extends Element>({
	virtualizer,
	snapshot,
	threshold,
	onStartReached,
	onEndReached
}: {
	virtualizer: AxisVirtualizer<TItem>
	snapshot: VirtualSnapshot
	threshold: VirtualThreshold
	onStartReached?: () => unknown
	onEndReached?: () => unknown
}) {
	useVirtualEdge({
		virtualizer,
		snapshot,
		edge: 'start',
		threshold,
		onReached: onStartReached
	})
	useVirtualEdge({
		virtualizer,
		snapshot,
		edge: 'end',
		threshold,
		onReached: onEndReached
	})
}

function useVirtualEdge<TItem extends Element>({
	virtualizer,
	snapshot,
	edge,
	threshold,
	onReached
}: {
	virtualizer: AxisVirtualizer<TItem>
	snapshot: VirtualSnapshot
	edge: 'start' | 'end'
	threshold: VirtualThreshold
	onReached?: () => unknown
}) {
	const firedCountRef = useRef(-1)
	const onReachedRef = useRef(onReached)
	onReachedRef.current = onReached

	const count = virtualizer.options.count
	const isEnabled = onReached !== undefined
	const items = snapshot.items
	const item = edge === 'start' ? items[0] : items.at(-1)

	useEffect(() => {
		if (count < firedCountRef.current) {
			firedCountRef.current = -1
		}

		if (!(isEnabled && onReachedRef.current && item)) {
			return
		}

		if (!isEdgeReached({ count, edge, item, threshold, virtualizer })) {
			firedCountRef.current = -1
			return
		}

		if (firedCountRef.current === count) {
			return
		}

		if (onReachedRef.current() !== false) {
			firedCountRef.current = count
		}
	}, [count, edge, isEnabled, item, threshold, virtualizer])
}

function isEdgeReached<TItem extends Element>({
	count,
	edge,
	item,
	threshold,
	virtualizer
}: {
	count: number
	edge: 'start' | 'end'
	item: ReturnType<AxisVirtualizer<TItem>['getVirtualItems']>[number]
	threshold: VirtualThreshold
	virtualizer: AxisVirtualizer<TItem>
}) {
	if ('items' in threshold) {
		return edge === 'start'
			? item.index <= threshold.items
			: item.index >= count - 1 - threshold.items
	}

	return edge === 'start'
		? (virtualizer.scrollOffset ?? 0) <= threshold.px
		: virtualizer.getTotalSize() - item.start - item.size <= threshold.px
}
